import { useMemo } from 'react';
import { useTenantContext } from './contexts.tsx';
import { useApiQuery } from './useApi.ts';

/**
 * Shell-facing tenant hook.
 * Adapts useTenantContext into the shape the shell app expects.
 *
 * Tenants are exposed with both `id` and `tenantId` (the same value) so
 * existing call sites that destructure `t.tenantId` keep working alongside
 * newer ones that read `t.id`.
 */
export interface TenantSummary {
  id: string;
  tenantId: string;
  name: string | undefined;
}

/**
 * Wire shape returned by the gateway tenant-list endpoint. We accept either
 * `id` or `tenantId` for the identifier (forward-compat with whichever the
 * gateway lane settles on) and tolerate a missing/empty name.
 *
 * CONVENTION (cross-lane): backed by `GET /api/v1/auth/tenants` returning the
 * principal's membership list. The gateway side is owned by the Tyndo.Gateway
 * lane; until it ships the query simply yields an empty list and the switcher
 * shows the current tenant only.
 */
interface TenantSummaryWire {
  id?: string;
  tenantId?: string;
  name?: string | null;
}

const TENANTS_PATH = '/auth/tenants';

const normalizeTenant = (t: TenantSummaryWire): TenantSummary | null => {
  const id = t.id ?? t.tenantId;
  if (!id) return null;
  return { id, tenantId: id, name: t.name ?? undefined };
};

export function useTenant() {
  const ctx = useTenantContext();

  // Fetch the principal's tenant membership from the gateway so the workspace
  // switcher is populated. Only runs when authenticated (a tenant is selected);
  // signed-out shells skip the call. Cached for the session so switching panes
  // doesn't refetch on every mount.
  const query = useApiQuery<TenantSummaryWire[]>(TENANTS_PATH, {
    enabled: Boolean(ctx.tenantId),
    staleTime: 5 * 60 * 1000,
  });

  return useMemo(() => {
    const current: TenantSummary | null = ctx.tenantId
      ? { id: ctx.tenantId, tenantId: ctx.tenantId, name: ctx.tenantName }
      : null;

    const fetched = Array.isArray(query.data)
      ? query.data
          .map(normalizeTenant)
          .filter((t): t is TenantSummary => t !== null)
      : [];

    // Ensure the current tenant is always present in the list even if the
    // membership fetch hasn't resolved yet (or omitted it), so the switcher
    // never renders empty while a tenant is active.
    let tenants = fetched;
    if (current && !fetched.some((t) => t.id === current.id)) {
      tenants = [current, ...fetched];
    } else if (current) {
      // Prefer the context's name for the active tenant if the fetch lacked one.
      tenants = fetched.map((t) =>
        t.id === current.id ? { ...t, name: t.name ?? current.name } : t,
      );
    }

    return {
      tenant: current,
      setTenant: ctx.setTenant,
      tenants,
      loading: query.isLoading,
    };
  }, [ctx.tenantId, ctx.tenantName, ctx.setTenant, query.data, query.isLoading]);
}
